import type { WarehouseMap, WarehouseMapArea, WarehouseMapShelf } from '@warehouse/shared';

/**
 * Geometry for the 2D map editor, as pure functions.
 *
 * The map is STRUCTURED ROWS (ARCHITECTURE §8.6): areas and shelves carry
 * integer grid coordinates, and this module is the only place that converts
 * between grid units and screen pixels. Keeping it pure means the drag maths —
 * the part that silently drifts by half a cell and nobody notices until a
 * shelf overlaps a pillar — is unit-testable without a DOM.
 */

/** One grid unit in CSS pixels at zoom 1. */
export const CELL_SIZE = 44;
export const MIN_ZOOM = 0.4;
export const MAX_ZOOM = 2;
/** Blank cells kept around the drawn objects so there is room to drag into. */
export const CANVAS_PADDING_CELLS = 2;

export interface GridRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface PixelRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

export function toPixels(rect: GridRect, zoom = 1): PixelRect {
  const cell = CELL_SIZE * zoom;
  return {
    left: rect.x * cell,
    top: rect.y * cell,
    width: rect.w * cell,
    height: rect.h * cell,
  };
}

/** Round a pixel delta to whole grid cells — objects always land on the grid. */
export function deltaToCells(deltaPx: number, zoom = 1): number {
  return Math.round(deltaPx / (CELL_SIZE * zoom));
}

/**
 * Where an object ends up after a drag. Never negative: the grid has an origin
 * and dragging past it must clamp, not produce a coordinate the server rejects.
 */
export function movedRect(rect: GridRect, deltaX: number, deltaY: number, zoom = 1): GridRect {
  return {
    ...rect,
    x: Math.max(0, rect.x + deltaToCells(deltaX, zoom)),
    y: Math.max(0, rect.y + deltaToCells(deltaY, zoom)),
  };
}

export function rectsOverlap(a: GridRect, b: GridRect): boolean {
  return a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;
}

/** Canvas size in cells, with padding, and never smaller than one screenful. */
export function canvasExtent(map: WarehouseMap | undefined): { cols: number; rows: number } {
  const areas = map?.areas ?? [];
  let cols = map?.width ?? 0;
  let rows = map?.height ?? 0;
  for (const area of areas) {
    for (const shelf of area.shelves) {
      cols = Math.max(cols, shelf.x + shelf.w);
      rows = Math.max(rows, shelf.y + shelf.h);
    }
  }
  return {
    cols: Math.max(12, cols + CANVAS_PADDING_CELLS),
    rows: Math.max(8, rows + CANVAS_PADDING_CELLS),
  };
}

/** How full a shelf is, 0..1 — drives the heat tint on the map. */
export function shelfFill(shelf: Pick<WarehouseMapShelf, 'binCount' | 'occupiedBinCount'>): number {
  if (shelf.binCount <= 0) return 0;
  return Math.min(1, shelf.occupiedBinCount / shelf.binCount);
}

/**
 * Tailwind classes for an area by kind/role. Floor areas read as flat surfaces,
 * storage areas as racking — the map has to be legible at a glance from three
 * metres away on a wall screen.
 */
export function areaTone(area: Pick<WarehouseMapArea, 'kind' | 'floorRole'>): string {
  if (area.kind === 'floor') {
    switch (area.floorRole) {
      case 'receiving':
        return 'border-sky-400/60 bg-sky-400/10';
      case 'packing':
        return 'border-amber-400/60 bg-amber-400/10';
      case 'dispatch':
        return 'border-emerald-400/60 bg-emerald-400/10';
      case 'production':
        return 'border-violet-400/60 bg-violet-400/10';
      default:
        return 'border-slate-400/60 bg-slate-400/10';
    }
  }
  return 'border-brand-500/50 bg-brand-500/5';
}

export const AREA_MIN_SIZE = 1;
export const AREA_MAX_SIZE = 500;

/** Clamp a resize/move so the server's zod bounds are never the first to complain. */
export function clampRect(rect: GridRect): GridRect {
  return {
    x: Math.min(Math.max(0, Math.round(rect.x)), AREA_MAX_SIZE),
    y: Math.min(Math.max(0, Math.round(rect.y)), AREA_MAX_SIZE),
    w: Math.min(Math.max(AREA_MIN_SIZE, Math.round(rect.w)), AREA_MAX_SIZE),
    h: Math.min(Math.max(AREA_MIN_SIZE, Math.round(rect.h)), AREA_MAX_SIZE),
  };
}
